import type { CalendarComposeProvider } from './calendarCompose.ts';
import type {
  NotificationChannel,
  NotificationDeliveryReason,
} from './notificationChannel.ts';

/** The unit a step's distance from the journey's start is counted in. */
export const JOURNEY_OFFSET_UNITS = ['days', 'weeks', 'months'] as const;
export type JourneyOffsetUnit = (typeof JOURNEY_OFFSET_UNITS)[number];

/** How long after the journey's start a step falls: `amount` `unit`s. */
export interface JourneyStepOffset {
  amount: number;
  unit: JourneyOffsetUnit;
}

/**
 * Where one step of a journey stands. Derived from the linked meeting, its
 * verslag and the dates every time it is read, never stored. `report_missing`
 * is a conversation that took place but has no verslag yet: not done.
 */
export const JOURNEY_STEP_STATUSES = [
  'to_plan',
  'action_required',
  'planned',
  'report_missing',
  'done',
] as const;
export type JourneyStepStatus = (typeof JOURNEY_STEP_STATUSES)[number];

/** Where a journey as a whole stands. Derived, like its steps. */
export const JOURNEY_STATUSES = ['running', 'done', 'stopped'] as const;
export type JourneyStatus = (typeof JOURNEY_STATUSES)[number];

/** One step of a journey type, as Wundo staff read and edit it. */
export interface JourneyTypeStepRecord {
  id: string;
  position: number;
  title: string;
  offset_amount: number;
  offset_unit: JourneyOffsetUnit;
  goal: string | null;
  /* Null only when its gesprekstype was deleted past the check that refuses it. */
  meeting_template_id: string | null;
}

/**
 * A journey type as Wundo staff read and edit it: the whole record, steps
 * included. `in_use` says journeys already follow it, which is when its steps
 * can still be edited but no longer added or removed.
 */
export interface JourneyTypeRecord {
  id: string;
  name: string;
  goal: string | null;
  margin_days: number;
  pre_warning_days: number;
  repeat_interval_days: number;
  max_repeats: number;
  in_use: boolean;
  steps: JourneyTypeStepRecord[];
}

export interface JourneyTypeRecordsResponse {
  data: JourneyTypeRecord[];
}

/**
 * One step of a journey type as the form sends it. With an `id` it is an
 * existing step being edited; without one it is a new step. The order of the
 * list is the order of the steps.
 */
export interface JourneyTypeStepRequest {
  id?: string;
  title: string;
  offset_amount: number;
  offset_unit: JourneyOffsetUnit;
  goal?: string | null;
  meeting_template_id: string;
}

/** A whole journey type as the form sends it, steps included. */
export interface JourneyTypeRequest {
  name: string;
  goal?: string | null;
  margin_days: number;
  pre_warning_days: number;
  repeat_interval_days: number;
  max_repeats: number;
  steps: JourneyTypeStepRequest[];
}

/**
 * One step of a journey as the staff console reads it: planning only.
 * `planned_date` is the linked meeting's date and nothing else about it.
 * `late` marks a planned meeting that falls after the deadline.
 */
export interface JourneyPlanningStep {
  id: string;
  position: number;
  title: string;
  target_date: string;
  deadline: string;
  status: JourneyStepStatus;
  late: boolean;
  planned_date: string | null;
}

/**
 * Where the manager's journey reminders would go, and why: `none` with its
 * reason means Wundo cannot reach them, which staff see when they set it up.
 */
export interface JourneyManagerReachability {
  channel: NotificationChannel;
  reason: NotificationDeliveryReason;
}

/**
 * A journey as the staff console reads it: who, when, and where each step
 * stands. Never what was said — no meeting titles, summaries or verslagen.
 */
export interface JourneyPlanningRecord {
  id: string;
  type: { id: string; name: string };
  manager: { id: string; name: string | null; email: string | null };
  manager_reachability: JourneyManagerReachability;
  employee_email: string;
  employee_name: string;
  start_date: string;
  status: JourneyStatus;
  stopped_at: string | null;
  created_at: string;
  steps: JourneyPlanningStep[];
}

export interface JourneyPlanningRecordsResponse {
  data: JourneyPlanningRecord[];
}

export interface JourneyCreateRequest {
  journey_type_id: string;
  manager_user_id: string;
  employee_email: string;
  employee_name: string;
  start_date: string;
}

/** The code a refused create carries when the pair already has a running journey. */
export const JOURNEY_ALREADY_RUNNING_CODE = 'journey_already_running';

/**
 * The 422 body for that refusal. `field` names the input to mark and
 * `existing_journey_id` the journey to link to, so the form can say which one
 * to stop first.
 */
export interface JourneyAlreadyRunningError {
  message: string;
  code: typeof JOURNEY_ALREADY_RUNNING_CODE;
  field: 'employee_email';
  existing_journey_id: string;
}

/**
 * Why the API refused a journey or journey type field that no schema rule
 * covers. It is the `code` of an entry in a 422's `issues`, whose `path` names
 * the field as a schema refusal does: `name`, `steps/2/offset_amount`.
 */
export const JOURNEY_ISSUE_CODES = [
  'name_taken',
  'journey_type_outside_organisation',
  'manager_outside_organisation',
  'template_outside_organisation',
  /* A journey step is a 1:1, so it follows a 1:1 gesprekstype. */
  'template_not_one_on_one',
  'offset_before_previous',
  'step_outside_type',
  'step_count_locked',
] as const;
export type JourneyIssueCode = (typeof JOURNEY_ISSUE_CODES)[number];

export interface JourneyIssue {
  path: string;
  code: JourneyIssueCode;
}

/**
 * Why the API refused a field of "Plan dit gesprek" or "Dit gesprek vond al
 * plaats" that no schema rule covers: a planned start already gone, or a held
 * day still to come. `path` is the body's own field.
 */
export const JOURNEY_STEP_ISSUE_CODES = ['in_the_past', 'in_the_future'] as const;
export type JourneyStepIssueCode = (typeof JOURNEY_STEP_ISSUE_CODES)[number];

export interface JourneyStepIssue {
  path: 'starts_at' | 'date';
  code: JourneyStepIssueCode;
}

/** The meeting linked to a step, as its manager sees it. */
export interface JourneyViewMeeting {
  id: string;
  title: string | null;
  starts_at: string;
}

/**
 * When and under what name a step's conversation is proposed, for the
 * planning dialog and the invite it drafts. Only an open step of a running
 * journey has one.
 */
export interface JourneyStepProposal {
  title: string;
  starts_at: string;
  ends_at: string;
}

/**
 * An invite the manager drafted from the step and Wundo waits to see in their
 * calendar, at the start it was drafted for. Only on a step without a meeting,
 * for two weeks after the draft.
 */
export interface JourneyPendingInvite {
  starts_at: string;
}

/**
 * One step of a journey as its manager reads it. `late` marks a planned
 * meeting that falls after the deadline. `is_first_open` marks the one step
 * Wundo works on now; only a running journey has one.
 */
export interface JourneyViewStep {
  id: string;
  position: number;
  title: string;
  goal: string | null;
  target_date: string;
  deadline: string;
  status: JourneyStepStatus;
  late: boolean;
  is_first_open: boolean;
  meeting: JourneyViewMeeting | null;
  proposal: JourneyStepProposal | null;
  pending_invite: JourneyPendingInvite | null;
}

/**
 * A journey as its manager reads it. The app writes the cadence sentence from
 * `cadence_offsets` and the counter from `current_position` of
 * `step_count`: the step not yet done, or the last one once all are.
 * `action_required` is set only while the journey runs.
 */
export interface JourneyView {
  id: string;
  type_name: string;
  goal: string | null;
  employee_name: string;
  employee_email: string;
  start_date: string;
  cadence_offsets: JourneyStepOffset[];
  current_position: number;
  step_count: number;
  status: JourneyStatus;
  action_required: boolean;
  stopped_at: string | null;
  steps: JourneyViewStep[];
}

/**
 * What Wundo found in the calendar for one open step. `suggestion` is the
 * meeting it is confident is this step's conversation, and null whenever it is
 * not. `candidates` are the meetings with the employee around the step, the
 * closest to its target date first, for "Andere afspraken tonen".
 */
export interface JourneyStepScreening {
  suggestion: JourneyMeetingOption | null;
  candidates: JourneyMeetingOption[];
}

/**
 * A step on the journey page. Only the first open step carries its screening
 * here; a later step is screened when it is expanded, through its own action.
 * `summary` is a done step's few sentences from its verslag, once written.
 */
export interface JourneyDetailStep extends JourneyViewStep, JourneyStepScreening {
  summary: string | null;
}

/**
 * One journey as its page reads it. `invite_provider` is the web calendar an
 * invite is drafted in. With `has_calendar` the sent invite comes back through
 * the sync, so planning writes nothing down in Wundo.
 */
export interface JourneyDetail extends Omit<JourneyView, 'steps'> {
  steps: JourneyDetailStep[];
  invite_provider: CalendarComposeProvider;
  has_calendar: boolean;
}

/** Running first, those needing action on top; then done, then stopped. */
export interface JourneyViewsResponse {
  data: JourneyView[];
}

/**
 * Which journey step a meeting is linked to, as the agenda and the meeting page
 * label it: "Opstarttraject · stap 3 van 5", linking to the journey.
 */
export interface JourneyMeetingLabel {
  journey_id: string;
  type_name: string;
  step_position: number;
  step_count: number;
}

/**
 * A meeting Wundo is confident is the conversation of a journey's first open
 * step, for the agenda's "Stap 3?" pill. Clicking it links the meeting to
 * `step_id`.
 */
export interface JourneyMeetingSuggestion {
  journey_id: string;
  step_id: string;
  step_position: number;
  type_name: string;
}

export interface JourneyLinkRequest {
  meeting_id: string;
}

/** Write a step's conversation down, for a manager without a calendar. */
export interface JourneyPlanRequest {
  starts_at: string;
}

/**
 * Keep the invite just drafted for a step, for a manager with a calendar: the
 * sync links the meeting it becomes once sent.
 */
export interface JourneyDraftInviteRequest {
  starts_at: string;
}

/**
 * Record an overdue step's conversation as already held, on `date`. A pasted
 * `report_text` becomes the material its verslag is written from.
 */
export interface JourneyRecordPastRequest {
  date: string;
  report_text?: string | null;
}

/** The meeting written down for the step, now linked to it. */
export interface JourneyStepMeetingResponse {
  meeting_id: string;
}

/** A meeting on the manager's calendar that a step can be linked to. */
export interface JourneyMeetingOption {
  id: string;
  title: string | null;
  starts_at: string;
  ends_at: string;
}

/**
 * The picker for one step. `with_employee` holds the meetings with the
 * employee around now, the coming weeks first. `search` holds the manager's
 * meetings matching the query by title or day, empty without a query. Neither
 * holds a cancelled meeting or one linked to a step already.
 */
export interface JourneyMeetingOptionsResponse {
  with_employee: JourneyMeetingOption[];
  search: JourneyMeetingOption[];
}
